import { Category } from '@gripp/shared-logic';
import { StyleProp, StyleSheet } from 'react-native';
import { Chip } from 'react-native-paper';
import { useMemo } from 'react';
import { Colors, useRoleHex } from '../../themes';

type CategoryChipProps = {
  category: Category;
  onPress?: (categoryId: string) => Promise<void>;
  additionalStyle?: StyleProp<any>;
};

export const CategoryChip = (props: CategoryChipProps) => {
  const onPress = async (categoryId: string) => {
    if (props.onPress) {
      await props.onPress(categoryId);
    }
  };

  const chipBase = useCategoryChipStyle();
  const chipStyle = props.additionalStyle
    ? [chipBase, props.additionalStyle]
    : [chipBase];

  return (
    <Chip
      onPress={async () => await onPress(props.category.id)}
      textStyle={styles.chipText}
      style={chipStyle}
      ellipsizeMode="tail"
    >
      {props.category.name}
    </Chip>
  );
};

const styles = StyleSheet.create({
  chipText: {
    lineHeight: 14,
    fontWeight: '400',
    color: Colors.role.textStrong,
    paddingVertical: 1,
    marginLeft: 10,
    marginRight: 10,
    marginTop: 5,
    marginBottom: 5,
  },
});

export const CategoryChipTextStyle = styles.chipText;

/**
 * Paper's `Chip` flattens the `style` it is handed and runs the resulting
 * `backgroundColor` through the `color` library (getChipColors ->
 * getSelectedBackgroundColor -> Color(...).mix(...)). A `var()` string is not
 * parseable, so it throws `Unable to parse color from string: var(--x)` and
 * takes the whole screen down through the error boundary.
 *
 * So unlike every other style object in the app, this one background cannot be
 * a CSS variable. `useRoleHex` resolves it to the mode's real hex and
 * re-renders on a mode flip. The text colour is fine as a variable: Paper does
 * no maths on `textStyle`.
 */
export const useCategoryChipStyle = () => {
  const backgroundColor = useRoleHex('surfaceHover');
  return useMemo(() => ({ backgroundColor }), [backgroundColor]);
};
